import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import ArchiveIcon from '@mui/icons-material/Archive';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { de as deLocale } from 'date-fns/locale';
import PageHeader from '../components/common/PageHeader';
import FormSkeleton from '../components/common/FormSkeleton';
import { useNotification } from '../hooks/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';
import * as P from '../constants/permissions';
import {
  uploadSessionsService,
  UploadSessionDetail,
} from '../services/uploadSessionsService';
import { format, parseISO } from 'date-fns';

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  neu: { label: 'Neu', color: 'default' },
  hochgeladen: { label: 'Hochgeladen', color: 'success' },
  abgelaufen: { label: 'Abgelaufen', color: 'error' },
};

const UploadSessionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(P.UPLOAD_SESSIONS_MANAGE);

  const [session, setSession] = useState<UploadSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editBeschreibung, setEditBeschreibung] = useState('');
  const [editGueltigBis, setEditGueltigBis] = useState<Date | null>(null);
  const [editSecret, setEditSecret] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSession = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await uploadSessionsService.get(Number(id));
      setSession(data);
    } catch {
      showError('Upload-Session nicht gefunden');
      navigate('/kundenuploads');
    } finally {
      setLoading(false);
    }
  }, [id, showError, navigate]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} in die Zwischenablage kopiert`);
    } catch {
      showError('Kopieren fehlgeschlagen');
    }
  };

  const getPublicLink = (token: string) => `${window.location.origin}/upload/${token}`;

  const formatDate = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy');
  const formatDateTime = (iso: string) => format(parseISO(iso), 'dd.MM.yyyy HH:mm');

  const formatFileSize = (bytes: number | null) => {
    if (bytes == null) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  if (loading) return <FormSkeleton />;
  if (!session) return null;

  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.neu;
  const hasFiles = session.dateien.length > 0;

  const openEditDialog = () => {
    setEditBeschreibung(session.beschreibung);
    setEditGueltigBis(parseISO(session.gueltig_bis));
    setEditSecret(session.secret);
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editBeschreibung.trim() || !editGueltigBis || editSecret.trim().length < 4) return;

    try {
      setSaving(true);
      const updated = await uploadSessionsService.update(session.id, {
        beschreibung: editBeschreibung.trim(),
        gueltig_bis: format(editGueltigBis, 'yyyy-MM-dd'),
        secret: editSecret.trim(),
      });
      setSession(updated);
      setEditOpen(false);
      showSuccess('Upload-Session aktualisiert');
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Fehler beim Speichern';
      showError(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Upload-Session"
        breadcrumbs={[
          { label: 'Kundenuploads', path: '/kundenuploads' },
          { label: session.beschreibung },
        ]}
        actions={canManage ? (
          <Button variant="outlined" startIcon={<EditIcon />} onClick={openEditDialog}>
            Bearbeiten
          </Button>
        ) : undefined}
      />

      <Grid container spacing={3}>
        {/* Session Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Beschreibung
                  </Typography>
                  <Typography variant="body1">{session.beschreibung}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip label={cfg.label} color={cfg.color} size="small" />
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Gültig bis
                  </Typography>
                  <Typography variant="body1">{formatDate(session.gueltig_bis)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Erstellt am
                  </Typography>
                  <Typography variant="body1">
                    {formatDateTime(session.erstellt_am)}
                    {session.erstellt_von && ` von ${session.erstellt_von.vorname} ${session.erstellt_von.nachname}`}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Credentials */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Upload-Link
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={getPublicLink(session.token)}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Link kopieren">
                  <IconButton onClick={() => copyToClipboard(getPublicLink(session.token), 'Link')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Passwort
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  value={session.secret}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Passwort kopieren">
                  <IconButton onClick={() => copyToClipboard(session.secret, 'Passwort')}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Files */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Hochgeladene Dateien ({session.dateien.length})
            </Typography>
            {hasFiles && (
              <Button
                component="a"
                href={uploadSessionsService.downloadAllZip(session.id)}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                startIcon={<ArchiveIcon />}
              >
                Alle als ZIP
              </Button>
            )}
          </Box>
          {session.dateien.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="text.secondary">Noch keine Dateien hochgeladen</Typography>
            </Paper>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dateiname</TableCell>
                    <TableCell>Größe</TableCell>
                    <TableCell>Hochgeladen am</TableCell>
                    <TableCell align="right">Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {session.dateien.map((f) => (
                    <TableRow key={f.id} hover>
                      <TableCell>{f.dateiname}</TableCell>
                      <TableCell>{formatFileSize(f.dateigroesse)}</TableCell>
                      <TableCell>{formatDateTime(f.hochgeladen_am)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Herunterladen">
                          <IconButton
                            size="small"
                            component="a"
                            href={uploadSessionsService.downloadFile(session.id, f.id)}
                            target="_blank"
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Upload-Session bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
          <TextField
            label="Beschreibung"
            value={editBeschreibung}
            onChange={(e) => setEditBeschreibung(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={deLocale}>
            <DatePicker
              label="Gültig bis"
              value={editGueltigBis}
              onChange={(newDate) => setEditGueltigBis(newDate)}
              minDate={new Date(Date.now() + 24 * 60 * 60 * 1000)}
              format="dd.MM.yyyy"
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          <TextField
            label="Passwort"
            value={editSecret}
            onChange={(e) => setEditSecret(e.target.value)}
            fullWidth
            helperText="Mindestens 4 Zeichen"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ color: 'text.secondary' }}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !editBeschreibung.trim() || !editGueltigBis || editSecret.trim().length < 4}
          >
            {saving ? 'Speichert...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadSessionDetailPage;
